import React, { useState, useEffect } from 'react';
import { Shield, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import '../styles/SessionIndicator.css';

const SessionIndicator = () => {
  const { isAuthenticated, user, token } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [sessionAge, setSessionAge] = useState('');

  useEffect(() => {
    // Show indicator briefly when user first loads while authenticated
    if (isAuthenticated && !isDismissed) {
      // Check if this is a returning session (token existed before page load)
      const wasAlreadyLoggedIn = sessionStorage.getItem('cadence_session_shown') !== 'true';
      
      if (wasAlreadyLoggedIn) {
        setIsVisible(true);
        sessionStorage.setItem('cadence_session_shown', 'true');
        
        // Auto-hide after 5 seconds
        const timer = setTimeout(() => {
          setIsVisible(false);
        }, 5000);
        
        return () => clearTimeout(timer);
      }
    }
  }, [isAuthenticated, isDismissed]);

  // Calculate session age
  useEffect(() => {
    if (!token) return;
    
    try {
      // Decode JWT to get issued time (iat)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const issuedAt = payload.iat * 1000; // Convert to milliseconds
      
      const updateAge = () => {
        const now = Date.now();
        const diff = now - issuedAt;
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const days = Math.floor(hours / 24);
        
        if (days > 0) {
          setSessionAge(`${days}d ago`);
        } else if (hours > 0) {
          setSessionAge(`${hours}h ago`);
        } else {
          setSessionAge('Just now');
        }
      };
      
      updateAge();
      const interval = setInterval(updateAge, 60000); // Update every minute
      
      return () => clearInterval(interval);
    } catch {
      setSessionAge('Active');
    }
  }, [token]);

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
  };

  const handleShow = () => {
    if (isAuthenticated && !isVisible) {
      setIsVisible(true);
      setIsDismissed(false);
      
      // Auto-hide after 5 seconds
      setTimeout(() => {
        setIsVisible(false);
      }, 5000);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <>
      {/* Collapsed indicator - always visible when logged in */}
      <button 
        className={`session-indicator-collapsed ${isVisible ? 'hidden' : ''}`}
        onClick={handleShow}
        aria-label="Show session info"
        data-testid="session-indicator-collapsed"
      >
        <Shield size={16} />
      </button>

      {/* Expanded indicator */}
      <div 
        className={`session-indicator ${isVisible ? 'visible' : ''}`}
        data-testid="session-indicator"
      >
        <div className="session-indicator-content">
          <Shield size={16} className="session-icon" />
          <div className="session-info">
            <span className="session-status">Session Active</span>
            <span className="session-details">
              {user?.first_name || user?.email?.split('@')[0] || 'User'} • {sessionAge}
            </span>
          </div>
          <button 
            className="session-dismiss"
            onClick={handleDismiss}
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </>
  );
};

export default SessionIndicator;
